import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  getEnabledCockpitOfferings,
  getRevenueAndSobHierarchyExpectedExit,
  getRevenueAndSobHierarchyRows,
  IRevenueAndSobExpectedExit,
  IRevenueAndSobHierarchyRow,
  TRevenueAndSobHierarchyOrder,
} from '../../lib'
import { selectCockpitOfferingsCatalog } from '../selectors'
import { IEnrichedCockpitFinSobRow } from '../types'

import { useFinSobAggregationInputs } from './useFinSobAggregationInputs'

export interface IUseFinSobHierarchyBreakdownResult {
  rows: IRevenueAndSobHierarchyRow[]
  /** The whole table's expected exit with its actuals/plan halves — one figure rather than one per
   *  row, see `getRevenueAndSobHierarchyExpectedExit`. All `null` for a period with nothing left
   *  open. */
  expectedExit: IRevenueAndSobExpectedExit
  currency: string
}

/** The expanded Revenue & SOB view's hierarchy breakdown — one row per Offering/Category/
 *  SubCategory/L3Category node, plus (manager cockpit mode only, via `showGeoHierarchy`) a Geo
 *  axis alongside it, ordered by `hierarchyOrder`. No synthetic Total row — see
 *  `getRevenueAndSobHierarchyRows`. Everything else comes from the same shared aggregation inputs
 *  `useFinSobCategoryBreakdown` uses. Offerings the partner doesn't have enabled are dropped before
 *  aggregation, so they neither get their own rows nor count towards the Total/Geo figures. */
export const useFinSobHierarchyBreakdown = (
  showGeoHierarchy: boolean,
  hierarchyOrder: TRevenueAndSobHierarchyOrder
): IUseFinSobHierarchyBreakdownResult => {
  const {
    targetRows,
    actualsRows,
    quotaRows,
    geoMappingItems,
    selectedFYear,
    actualsType,
    isLocalCurrency,
    isYtd,
    currentFQuarter,
    fQuarterActualsPercentage,
    isPeriodOpen,
    isPrevFYearClosing,
    currency,
  } = useFinSobAggregationInputs()

  const offeringsCatalog = useSelector(selectCockpitOfferingsCatalog)

  const enabledOfferings = useMemo(
    () =>
      new Set(getEnabledCockpitOfferings(offeringsCatalog).map(({ offering }) => offering.trim())),
    [offeringsCatalog]
  )

  /** `offering` is compared trimmed on both sides, the same way `getFilteredFinSobRows` does. The
   *  catalog loads asynchronously (`offeringsCatalog` starts empty), so an empty set means "not
   *  loaded yet" rather than "nothing is enabled" — filtering on it would blank the table. */
  const filterEnabled = useCallback(
    (rows: IEnrichedCockpitFinSobRow[]) =>
      enabledOfferings.size === 0
        ? rows
        : rows.filter((row) => enabledOfferings.has(row.offering.trim())),
    [enabledOfferings]
  )

  const enabledTargetRows = useMemo(() => filterEnabled(targetRows), [filterEnabled, targetRows])
  const enabledActualsRows = useMemo(() => filterEnabled(actualsRows), [filterEnabled, actualsRows])
  const enabledQuotaRows = useMemo(() => filterEnabled(quotaRows), [filterEnabled, quotaRows])

  const rows = useMemo(
    () =>
      getRevenueAndSobHierarchyRows({
        targetRows: enabledTargetRows,
        actualsRows: enabledActualsRows,
        quotaRows: enabledQuotaRows,
        showGeoHierarchy,
        geoMappingItems,
        hierarchyOrder,
        selectedFYear,
        actualsType,
        isLocalCurrency,
        isYtd,
        currentFQuarter,
        fQuarterActualsPercentage,
        isPeriodOpen,
        isPrevFYearClosing,
      }),
    [
      enabledTargetRows,
      enabledActualsRows,
      enabledQuotaRows,
      showGeoHierarchy,
      geoMappingItems,
      hierarchyOrder,
      selectedFYear,
      actualsType,
      isLocalCurrency,
      isYtd,
      currentFQuarter,
      fQuarterActualsPercentage,
      isPeriodOpen,
      isPrevFYearClosing,
    ]
  )

  /** Built from the same enabled-offering rows as `rows`, so it is the Total row's figure. */
  const expectedExit = useMemo(
    () =>
      getRevenueAndSobHierarchyExpectedExit({
        targetRows: enabledTargetRows,
        actualsRows: enabledActualsRows,
        selectedFYear,
        currentFQuarter,
        actualsType,
        isLocalCurrency,
        isPeriodOpen,
        isPrevFYearClosing,
      }),
    [
      enabledTargetRows,
      enabledActualsRows,
      selectedFYear,
      currentFQuarter,
      actualsType,
      isLocalCurrency,
      isPeriodOpen,
      isPrevFYearClosing,
    ]
  )

  return { rows, expectedExit, currency }
}
